// @ts-nocheck
/**
 * LiveSheet — spreadsheet-style grid over event_registrations_v2.
 *
 * One row per registered team. Writes go through the grid-batch-update edge
 * function under the caller's JWT. The editable column set below MUST stay in
 * sync with that function's allowlist — the server decides, this is only UI.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CompactSelection,
  DataEditor,
  GridCellKind,
  type EditableGridCell,
  type GridCell,
  type GridColumn,
  type GridSelection,
  type Item,
} from "@glideapps/glide-data-grid";

import simpleDropdownCellRenderer from "@/components/events/cells/simpleDropdownCell";
import { ExternalLink, Share2, Plus } from "lucide-react";
import "@glideapps/glide-data-grid/dist/index.css";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import RegistrationPaymentDialog from "@/components/events/RegistrationPaymentDialog";
import ShareSheetDialog from "@/components/events/ShareSheetDialog";
import {
  CHECKIN_COLUMN_IDS,
  MONEY_COLUMN_IDS,
  fetchSheetDatasets,
  type ShareInfo,
} from "@/components/events/sheetSource";
import CompRegistrationDialog from "@/components/events/CompRegistrationDialog";
import WithdrawTeamDialog from "@/components/events/WithdrawTeamDialog";
import { ConfirmTrackedTeamDialog } from "@/components/events/ConfirmTrackedTeamDialog";
import { type PastedTeam } from "@/components/events/PasteTrackedTeamsDialog";
import AddTeamsDialog from "@/components/events/AddTeamsDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/** Read-only columns whose text routinely overflows: click opens the full value. */
const EXPANDABLE_COLUMN_IDS = new Set(["schedule_request", "extras"]);
import { getRlTeams } from "@/lib/rlTeams";
import { formatCalendarDate, formatTime } from "@/lib/dates";
import { getSessionUser } from '@/lib/authSession';



const CUSTOM_CELLS = [simpleDropdownCellRenderer];

const TABLE = "event_registrations_v2";
/** Teams a director is tracking (pay-at-door, invited, verbal yes) live here. */
const TRACKING_TABLE = "event_team_tracking";


/**
 * Exactly the edge function allowlist. Nothing else is editable.
 *
 * Money is deliberately absent: total_amount_paid, payment_status and
 * payment_received_at are derived by a trigger from the registration_payments
 * ledger, so a director records a transaction through the row's Payments
 * action instead of typing into a cell.
 */
const EDITABLE = new Set([
  // Renames the team's permanent record, handled server-side.
  "team_name",
  "age_group",
  "pool",
  "coach_first_name",
  "coach_last_name",
  "coach_email",
  "coach_phone",
  "primary_billing_email",
  "admin_notes",

  "registration_status",
  "payment_method_chosen",
  "roster_status",
  "hotel_status",
  "hotel_rooms",
  "updates_responses",
]);



const REGISTRATION_STATUSES = [
  "draft",
  "pending_payment",
  "registered",
  "waitlisted",
  "dropped",
  "checked_in",
  "completed",
  "refunded",
];

/** A withdrawn team is still on the sheet, just out of the active counts. */
const WITHDRAWN_STATUSES = new Set(["dropped", "refunded"]);

/** The four a director actually uses when recording pay-later money. */
const PAYMENT_STATUSES = ["unpaid", "partial", "paid", "refunded"];

const PAYMENT_METHODS = ["online", "check", "cash", "venmo"];

const TRACKED_BADGE_BG = "#5a6d8a"; // muted blue-slate
const TRACKED_BADGE_FG = "#ffffff";

function drawTrackedBadge(ctx: CanvasRenderingContext2D, rect: any, size = 18) {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const r = size / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = TRACKED_BADGE_BG;
  ctx.fill();
  ctx.font = "700 11px sans-serif";
  ctx.fillStyle = TRACKED_BADGE_FG;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("T", cx, cy + 0.5);
  ctx.restore();
}

const STRIPE_LOCK_REASON =
  "This payment came through Stripe and must be adjusted in Stripe — editing it here would put your books out of sync with your processor.";

/**
 * Mirrors the server guard: an online registration with a completed Stripe
 * session is processor-owned, so its money cells stay read-only.
 */
const stripeLocked = (row: any) =>
  String(row?.payment_method_chosen ?? "").toLowerCase() === "online" &&
  Boolean(row?.stripe_payment_intent_id || row?.stripe_session_id);

const moneyGuard = (row: any) => (stripeLocked(row) ? STRIPE_LOCK_REASON : null);

type SheetColumn = {
  id: string;
  title: string;
  width?: number;
  kind?: "text" | "number" | "boolean" | "money" | "date" | "action";
  editable?: boolean;
  optionsKey?:
    | "age_group"
    | "level"
    | "registration_status"
    | "payment_status"
    | "payment_method";
  /** Returns a reason string when this row's cell must stay read-only. */
  guard?: (row: any) => string | null;
  /** Header click sorts the display only — pending edits are untouched. */
  sortable?: boolean;
  /** Off until the director turns it on: always-empty or always-identical columns. */
  hiddenByDefault?: boolean;
};

/** Titles match the director's existing spreadsheet headings verbatim. */
const COLUMNS: SheetColumn[] = [
  // Gutter flag: a tinted "T" on tracked (not-yet-registered) rows. Sits next
  // to the row numbers so it costs almost no horizontal space.
  { id: "__t", title: "", width: 28, kind: "action", sortable: false },
  { id: "__payments", title: "Payments", width: 90, kind: "action" },
  // Comp / un-comp this registration.
  { id: "__comp", title: "Comp", width: 70, kind: "action" },
  // Withdraw a team from the event (or restore one) — the row stays visible.
  { id: "__withdraw", title: "Withdraw", width: 90, kind: "action" },
  // Tracked (unregistered) teams only: turn the lead into a real registration
  // so the scheduler can use it.
  { id: "__confirm", title: "Confirm", width: 84, kind: "action" },

  // Editing this renames the team's record everywhere, not just this event.
  { id: "team_name", title: "Team", width: 220, editable: true, sortable: true },

  {
    id: "age_group",
    title: "Division",
    width: 110,
    editable: true,
    optionsKey: "age_group",
    sortable: true,
  },
  { id: "coach_first_name", title: "Coach First", width: 120, editable: true },
  { id: "coach_last_name", title: "Coach Last", width: 120, editable: true },
  { id: "coach_email", title: "Email", width: 210, editable: true },
  { id: "coach_phone", title: "Phone", width: 140, editable: true },
  { id: "schedule_request", title: "Schedule Request", width: 240 },
  { id: "pool", title: "Rate Your Team", width: 150, editable: true, optionsKey: "level" },
  { id: "extras", title: "Extras", width: 220 },
  { id: "registered_at", title: "Registered", width: 130, kind: "date", sortable: true },
  {
    id: "registration_status",
    title: "Registration Status",
    width: 160,
    editable: true,
    optionsKey: "registration_status",
    // "registered" on every row until someone withdraws or is refunded.
    hiddenByDefault: true,
  },
  // Paid, Amount Paid and Balance are ledger-derived — read-only by design.
  { id: "payment_status", title: "Paid", width: 130, sortable: true },
  { id: "total_amount_due", title: "Total", width: 120, kind: "money", sortable: true },
  { id: "total_amount_paid", title: "Amount Paid", width: 130, kind: "money", sortable: true },
  { id: "amount_owed", title: "Balance", width: 130, kind: "money", sortable: true },
  {
    id: "payment_method_chosen",
    title: "Payment Method",
    width: 150,
    editable: true,
    optionsKey: "payment_method",
    guard: moneyGuard,
  },
  { id: "payment_received_at", title: "Payment Received", width: 150, kind: "date" },

  { id: "special_requests_fee", title: "Request Fees", width: 120, kind: "money" },
  { id: "extras_total", title: "Extras Total", width: 120, kind: "money" },
  { id: "discount_total", title: "Discount", width: 110, kind: "money" },
  // 3% card processing fee — card payments only, part of the balance.
  { id: "processing_fee", title: "Card Fee", width: 110, kind: "money" },
  { id: "primary_billing_email", title: "Registrant Email", width: 210, editable: true },

  // Outreach tracking, deliberately free text — these mirror the columns the
  // director works from day to day and apply to tracked teams as well.
  { id: "roster_status", title: "Roster Outreach", width: 180, editable: true },
  { id: "hotel_status", title: "Hotel Status", width: 160, editable: true },
  { id: "hotel_rooms", title: "Hotel Rooms", width: 160, editable: true },
  { id: "updates_responses", title: "Updates / Responses", width: 260, editable: true },

  // The director's own working column; the coach's signup notes live on the
  // registration record, not here.
  { id: "admin_notes", title: "Notes", width: 260, editable: true },
];

/**
 * The blank row at the bottom takes these, mapped onto event_team_tracking.
 * Nothing is written until a team name is typed.
 */

/** Free-text columns a tracked (not-yet-registered) row can still edit. */
const TRACKED_EDITABLE = new Set([
  "team_name",
  "roster_status",
  "hotel_status",
  "hotel_rooms",
  "updates_responses",
  "admin_notes",
  "coach_email",
  "coach_phone",
  "coach_first_name",
  "coach_last_name",
  "age_group",
  "total_amount_due",
]);

/** Grid column id → event_team_tracking column. */
const TRACKED_FIELD_MAP: Record<string, string> = {
  team_name: "team_name",
  age_group: "division_label",
  coach_email: "contact_email",
  coach_phone: "contact_phone",
  total_amount_due: "expected_amount",
  roster_status: "roster_status",
  hotel_status: "hotel_status",
  hotel_rooms: "hotel_rooms",
  updates_responses: "updates_responses",
  admin_notes: "admin_notes",
};

const COLUMN_BY_ID = new Map(COLUMNS.map((c) => [c.id, c]));

/** Chrome-less popup route for one event's grid. */
export const sheetWindowPath = (eventId: string) => `/events/${eventId}/sheet`;





type Row = Record<string, unknown> & { id: string };
type EditKey = `${string}::${string}`;
type Options = Record<string, string[]>;

const num = (v: unknown) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};



const asMoney = (v: unknown) =>
  v == null || v === ""
    ? ""
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));

const fmtDate = (v: unknown) => (v ? formatCalendarDate(String(v), "M/d/yyyy") : "");

const fmtShortDate = (v: unknown) => {
  if (!v) return "";
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const fmtShortTime = (v: unknown) => formatTime(v as string | null | undefined);

/**
 * extras_summary is a cart snapshot — it also carries base_price, constraints
 * and discount_policy. Only the purchased items belong in this column.
 */
function formatExtras(summary: unknown): string {
  const items = (summary as any)?.extras;
  if (!Array.isArray(items) || items.length === 0) return "";
  return items
    .map((e: any) => {
      const name = String(e?.name ?? "").trim();
      if (!name) return "";
      const qty = Number(e?.qty ?? 1) || 1;
      const line = Number(e?.price ?? 0) * qty;
      return `${name} ×${qty}${line ? ` · ${asMoney(line)}` : ""}`;
    })
    .filter(Boolean)
    .join(" · ");
}

/**
 * Phones are stored exactly as captured (E.164). US numbers read as
 * (816) 223-7452 in the grid; anything else is shown untouched. Display only —
 * Twilio dials the stored value, so nothing here is written back.
 */
const fmtPhone = (v: unknown) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const d = s.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return s;
};


/** Either format is accepted on edit; US numbers are stored as +1XXXXXXXXXX. */
const normalizePhoneInput = (v: unknown) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const d = s.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return s.startsWith("+") ? s : d ? `+${d}` : "";
};

/**
 * Scheduling requests bought during registration live in the cart snapshot
 * (extras_summary.constraints) — schedule_constraints rows only exist once an
 * operator has processed them, so the sheet reads both.
 */
function summaryConstraintNotes(
  summary: unknown,
  teamNameById: Record<string, string>
): string[] {
  const list = (summary as any)?.constraints;
  if (!Array.isArray(list)) return [];
  return list
    .map((c: any) => {
      const label =
        String(c?.template_label || humanizeType(c?.constraint_type) || "").trim() || "Request";
      const p = (c?.params && typeof c.params === "object" ? c.params : {}) as Record<string, any>;
      const specs = Array.isArray(c?.input_field_specs) ? c.input_field_specs : [];
      const dates: string[] = [];
      const times: string[] = [];
      const others: string[] = [];
      for (const spec of specs) {
        const key = spec?.field_key;
        if (!key) continue;
        let val = p[`${key}__display`] ?? p[key];
        if (val == null || val === "") continue;
        val = String(val).replace(/\s*\(this registration\)\s*$/, "").trim();
        if (spec.input_type === "date") dates.push(fmtShortDate(val));
        else if (spec.input_type === "time") times.push(fmtShortTime(val));
        else {
          // Team pickers store ids; cart placeholders carry a display name instead.
          const resolved = teamNameById[val] ?? val;
          if (/^cart:/.test(resolved)) continue;
          others.push(resolved);
        }
      }
      const detail = [dates.join(" "), times.join("–"), ...others].filter(Boolean).join(" ");
      return detail ? `${label} — ${detail}` : label;
    })
    .filter(Boolean);
}

/** Payment-status palette shared by the $ badge. */
const payTone = (status: unknown) => {
  const s = String(status ?? "").toLowerCase();
  if (s === "paid" || s === "comped") return { bg: "#dcfce7", fg: "#166534" };
  if (s === "partial") return { bg: "#eef2f7", fg: "#475569" };
  if (s === "refunded" || s === "partially_refunded") return { bg: "#ede9fe", fg: "#5b21b6" };
  return { bg: "#fee2e2", fg: "#b91c1c" };
};

function humanizeType(t: string): string {

  const s = String(t || "").replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

function constraintNote(
  constraintType: string,
  parameters: any,
  teamNameById: Record<string, string>
): string {
  const p = (parameters && typeof parameters === "object" ? parameters : {}) as Record<string, any>;

  if (constraintType === "must_play_before" || constraintType === "must_play_after") {
    const label = constraintType === "must_play_before" ? "Must play before" : "Must play after";
    const date = fmtShortDate(p.target_date ?? p.date);
    const time = fmtShortTime(p.target_time ?? p.time);
    return [label, date && `${date},`, time].filter(Boolean).join(" ").replace(/,$/, "");
  }

  if (constraintType === "do_not_play") {
    const name = (p.related_team_id && teamNameById[p.related_team_id]) || "another team";
    return `Do not play ${name}`;
  }

  return humanizeType(constraintType);
}


export default function LiveSheet({
  eventId,
  /** True when rendered by the popup route — it is already its own window. */
  popout = false,
  /** Set when the sheet is opened through a share link, not by its owner. */
  shareToken,
  /** Recipient-facing message when the share link is refused. */
  onDenied,
}: {
  eventId: string;
  popout?: boolean;
  shareToken?: string;
  onDenied?: (message: string) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [ageGroups, setAgeGroups] = useState<string[]>([]);
  const [options, setOptions] = useState<Options>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState<Map<EditKey, unknown>>(new Map());
  const [failedRows, setFailedRows] = useState<Map<string, string>>(new Map());
  const [status, setStatus] = useState<string | null>(null);
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const [payRow, setPayRow] = useState<{ id: string; name: string } | null>(null);
  const [sort, setSort] = useState<{ id: string; dir: "asc" | "desc" } | null>(null);
  /** Dropouts stay one click away without cluttering the working sheet. */
  const [teamFilter, setTeamFilter] = useState<"active" | "withdrawn" | "all">("active");
  const [withdrawRow, setWithdrawRow] = useState<Row | null>(null);
  const [withdrawTip, setWithdrawTip] = useState<string | null>(null);
  // Hover preview and click-through overlay for cells that overflow.
  const [cellTip, setCellTip] = useState<{ title: string; text: string } | null>(null);
  const [expandCell, setExpandCell] = useState<{ title: string; text: string } | null>(null);
  const [userId, setUserId] = useState("");
  const [share, setShare] = useState<ShareInfo | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  /** A view link is read-only everywhere: no editor, no row actions, no save. */
  const readOnly = Boolean(shareToken) && share?.permission !== "edit";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await getSessionUser();
      if (cancelled) return;
      setUserId(data?.user?.id ?? "");
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);


  /** Root element — the paste handler listens here. */
  const shellRef = useRef<HTMLDivElement | null>(null);

  /* --------------------- open in new window --------------------- */
  // A director runs several events at once, so the grid opens as its own
  // window rather than taking over this one. The name is per event: clicking
  // again focuses the window that is already open instead of duplicating it.
  const openInNewWindow = useCallback(() => {
    const w = window.open(
      sheetWindowPath(eventId),
      `livesheet-${eventId}`,
      "popup=yes,width=1600,height=900,resizable=yes,scrollbars=yes"
    );
    w?.focus();
  }, [eventId]);

  const editsRef = useRef(edits);
  editsRef.current = edits;

  /* ---------------------------- load ---------------------------- */

  const load = useCallback(async () => {
    // Organiser reads the tables directly; a share-link recipient gets the
    // same shapes back from the server with the token's rules already applied.
    const ds = await fetchSheetDatasets(eventId, shareToken);

    if (ds.denied) {
      setDenied(ds.denied);
      onDenied?.(ds.denied);
      return null;
    }
    if (ds.error) {
      setStatus(`Couldn't load registrations: ${ds.error}`);
      return null;
    }
    if (ds.share) setShare(ds.share);

    const base = ds.registrations;

    // Team names, divisions and gender come from RecruitLook, which owns the
    // team records. One batched call per load, cached in memory for the
    // session and never persisted. If RL is unavailable the sheet still loads —
    // it just falls back to the name captured on the registration.
    let rlTeamById: Record<string, any> = {};
    try {
      const ids = [
        ...new Set(
          (base as any[])
            .map((r) => r.recruitlook_team_id || r.team_id)
            .filter(Boolean)
            .map(String)
        ),
      ];
      if (ids.length > 0) rlTeamById = await getRlTeams(ids);
    } catch (err) {
      console.error("RecruitLook team lookup failed", err);
    }
    const rlTeam = (r: any) =>
      rlTeamById[String(r.recruitlook_team_id || r.team_id || "")] ?? null;

    const coachIdFor = (r: any) => r.coach_user_id || r.registered_by_user_id || null;
    const profiles = ds.profiles;

    const dedupe = (vals: string[]) => {
      const seen = new Map<string, string>();
      for (const v of vals) if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
      return [...seen.values()];
    };
    const ageNames = dedupe(ds.ageGroups);
    setAgeGroups(ageNames);
    setOptions({
      age_group: ageNames,
      level: dedupe(ds.levels),
      registration_status: REGISTRATION_STATUSES,
      payment_method: PAYMENT_METHODS,
      payment_status: PAYMENT_STATUSES,
    });

    // Team names for resolving related teams in constraints.
    const teamNameById: Record<string, string> = {};
    for (const r of base as any[]) {
      if (r.team_id) {
        teamNameById[r.team_id] =
          rlTeam(r)?.display_name || r.team_name_snapshot || "";
      }
    }

    // Schedule requests keyed by team.
    const constraints = ds.constraints;
    const requestsByTeam: Record<string, string[]> = {};
    for (const c of constraints ?? []) {
      const note = constraintNote((c as any).constraint_type, (c as any).parameters, teamNameById);
      if (!note || !(c as any).team_id) continue;
      (requestsByTeam[(c as any).team_id] ||= []).push(note);
    }

    // Latest withdrawal note per registration — shown on hover. Agreed refunds
    // that nobody has sent yet stay owed until an organiser marks them sent.
    const withdrawalByReg: Record<string, string> = {};
    const refundOwedByReg: Record<string, number> = {};
    {
      const withdrawals = ds.withdrawals;
      for (const w of withdrawals ?? []) {
        if ((w as any).status === "cancelled_by_user") continue;
        withdrawalByReg[(w as any).registration_id] =
          [(w as any).reason?.replace(/_/g, " "), (w as any).reason_details]
            .filter(Boolean)
            .join(" — ");
        refundOwedByReg[(w as any).registration_id] = (w as any).refund_processed_at
          ? 0
          : Number((w as any).refund_amount_cents ?? 0) / 100;
      }
    }


    const registrationRows = base.map((r: any) => {
      const coach = profiles[coachIdFor(r) as string] ?? null;
      const extras = formatExtras(r.extras_summary);

      const due = Number(r.total_amount_due ?? 0);
      const paid = Number(r.total_amount_paid ?? 0);
      const rt = rlTeam(r);
      const teamName = rt?.display_name || r.team_name_snapshot || r.team_name || "";
      const comped = Boolean(r.comped_at);
      const withdrawn = WITHDRAWN_STATUSES.has(
        String(r.registration_status ?? "").toLowerCase()
      );
      return {
        ...r,
        // Registration-level overrides win; the linked profile is only a fallback,
        // and editing these never touches the coach's own profile record.
        coach_phone: r.coach_phone || coach?.phone || "",
        coach_first_name: r.coach_first_name || coach?.first_name || "",
        coach_last_name: r.coach_last_name || coach?.last_name || "",
        coach_email: r.coach_email || coach?.email || "",
        team_name: teamName,

        schedule_request: [
          ...(requestsByTeam[r.team_id] ?? []),
          ...summaryConstraintNotes(r.extras_summary, teamNameById),
        ]
          // The same request can exist in both places once an operator saves it.
          .filter((v, i, a) => v && a.indexOf(v) === i)
          .join(" · "),
        extras,
        paid_yn: String(r.payment_status ?? "").toLowerCase() === "paid" ? "Yes" : "No",
        // A comped team owes nothing by definition.
        amount_owed: comped
          ? 0
          : Math.max(0, due + num(r.processing_fee) + num(r.late_fee_charged) - paid),
        __withdrawn: withdrawn,
        __withdraw_reason: withdrawalByReg[r.id] ?? "",
        __refund_owed: refundOwedByReg[r.id] ?? 0,
      } as Row;
    });



    // Tracked teams (pay-at-door, invited, verbal yes) sit in the same grid so
    // a director works one sheet. They aren't registrations, so their cells are
    // read-only here until they convert.
    const trackedRows = ds.tracked.map((t: any) => {
      const due = Number(t.expected_amount ?? 0);
      const paid = Number(t.amount_received ?? 0);
      return {
        id: t.id,
        __tracked: true,
        team_name: t.team_name ?? "",
        age_group: t.division_label ?? "",
        coach_first_name: t.contact_name ?? "",
        coach_last_name: "",
        coach_email: t.contact_email ?? "",
        coach_phone: t.contact_phone ?? "",
        schedule_request: "",
        extras: "",
        registered_at: t.created_at,
        registration_status: `tracked · ${String(t.status ?? "")}`,
        payment_status: t.payment_status ?? "unpaid",
        payment_method_chosen: t.payment_method ?? "",
        total_amount_due: due,
        total_amount_paid: paid,
        amount_owed: Math.max(0, due - paid),
        admin_notes: t.admin_notes ?? t.internal_notes ?? "",
        roster_status: t.roster_status ?? "",
        hotel_status: t.hotel_status ?? "",
        hotel_rooms: t.hotel_rooms ?? "",
        updates_responses: t.updates_responses ?? "",
        __withdrawn: Boolean(t.withdrawn_at),
        __withdraw_reason: "",
        // The confirm dialog prefills from the tracked record itself.
        __raw: t,
      } as Row;

    });

    return [...registrationRows, ...trackedRows];
  }, [eventId, shareToken]);


  const reload = useCallback(async () => {
    const next = await load();
    if (next) setRows(next);
  }, [load]);

  /* --------------------- creating tracked teams ------------------ */

  /**
   * Insert tracked rows from the Add teams dialog — the only way in.
   * Values are stored verbatim; matching to a real team happens later, in the
   * Confirm dialog.
   */
  const createTracked = useCallback(
    async (teams: PastedTeam[]) => {
      if (teams.length === 0) return false;

      // No parsing, no matching — what the organiser typed is what is stored.
      // These are leads: they stay out of the schedule until confirmed, where
      // matching to a real team happens.
      const payload = teams.map((t) => ({
        event_id: eventId,
        team_name: String(t.team_name ?? "").trim().slice(0, 200),
        division_label: t.division_label || null,
        contact_name: t.contact_name || null,
        contact_email: t.contact_email || null,
        contact_phone: t.contact_phone || null,
        expected_amount: t.expected_amount,
        include_in_schedule: false,
        added_by: userId || null,
      }));

      const { error } = await supabase.from(TRACKING_TABLE).insert(payload as any);
      if (error) {
        toast.error(`Couldn't add tracked teams: ${error.message}`);
        return false;
      }
      await reload();
      toast.success(
        `Added ${payload.length} tracked ${payload.length === 1 ? "team" : "teams"}.`
      );
      return true;
    },
    [eventId, userId, reload]
  );

  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [compRow, setCompRow] = useState<Row | null>(null);
  const [confirmRow, setConfirmRow] = useState<any | null>(null);
  const [compTip, setCompTip] = useState<string | null>(null);
  const [trackedHeaderTip, setTrackedHeaderTip] = useState<{ x: number; y: number } | null>(null);
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);
  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  });

  const handleAddTeams = useCallback(
    async (teams: PastedTeam[]) => {
      setAdding(true);
      const ok = await createTracked(teams);
      setAdding(false);
      return ok;
    },
    [createTracked]
  );





  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const next = await load();
      if (cancelled) return;
      if (next) setRows(next);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, load]);

  /* -------------------------- realtime -------------------------- */

  useEffect(() => {
    // A share recipient has no RLS access to the table, so realtime would
    // deliver nothing — they refresh by reopening the link.
    if (shareToken) return;
    const channel = supabase
      .channel(`livesheet:${eventId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: TABLE, filter: `event_id=eq.${eventId}` },
        () => {
          // Joined coach/schedule fields aren't in the payload — reload.
          // But never blow away in-flight edits.
          if (editsRef.current.size > 0) return;
          reload();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, reload, shareToken]);

  /* --------------------------- derived -------------------------- */

  const filteredRows = useMemo(() => {
    let scoped = rows.filter((r) =>
      teamFilter === "all"
        ? true
        : teamFilter === "withdrawn"
        ? Boolean(r.__withdrawn)
        : !r.__withdrawn
    );
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((r) =>
      [
        r.team_name,
        `${r.coach_first_name ?? ""} ${r.coach_last_name ?? ""}`,
        r.coach_email,
        r.age_group,
        r.pool,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [rows, search, teamFilter]);

  /**
   * Sorting is display-only: it reorders this array and nothing else. Edits are
   * keyed by row id, so a pending change follows its row wherever it lands.
   */
  const visibleRows = useMemo(() => {
    if (!sort) return filteredRows;
    const def = COLUMN_BY_ID.get(sort.id);
    const numeric = def?.kind === "money" || def?.kind === "number";
    const dated = def?.kind === "date";
    const dir = sort.dir === "asc" ? 1 : -1;
    const valueOf = (r: Row) =>
      sort.id === "amount_owed" ? num(r.amount_owed) : r[sort.id];
    return [...filteredRows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (numeric) return (num(av) - num(bv)) * dir;
      if (dated) {
        const at = av ? new Date(String(av)).getTime() : 0;
        const bt = bv ? new Date(String(bv)).getTime() : 0;
        return (at - bt) * dir;
      }
      return String(av ?? "").localeCompare(String(bv ?? ""), undefined, { numeric: true }) * dir;
    });
  }, [filteredRows, sort]);





  const stats = useMemo(() => {
    // Withdrawn teams are visible but never counted as active.
    const active = rows.filter((r) => !r.__withdrawn);
    const withdrawn = rows.length - active.length;
    const total = active.length;
    let paid = 0;
    const byAge: Record<string, number> = {};
    for (const g of ageGroups) byAge[g] = 0;
    for (const r of active) {
      if (String(r.payment_status ?? "").toLowerCase() === "paid") paid += 1;
      const g = String(r.age_group ?? "");
      const match = ageGroups.find((a) => a.toLowerCase() === g.toLowerCase());
      if (match) byAge[match] += 1;
    }
    const tracked = active.filter((r) => r.__tracked).length;
    // Agreed-but-unsent refunds: money the organiser still has to send out.
    const owedRows = rows.filter((r) => num(r.__refund_owed) > 0);
    const refundsOwedCount = owedRows.length;
    const refundsOwedTotal = owedRows.reduce((s, r) => s + num(r.__refund_owed), 0);
    return {
      total,
      paid,
      pending: total - paid,
      withdrawn,
      tracked,
      byAge,
      refundsOwedCount,
      refundsOwedTotal,
    };
  }, [rows, ageGroups]);


  /* ------------------------ grid plumbing ----------------------- */

  // Fixed column order — no user configuration. A share link drops the
  // columns its token hides, and every row action on a view-only link.
  // Confirm only does something on tracked rows, so the column is dropped
  // entirely when the current filter shows none.
  const hasTrackedVisible = useMemo(
    () => filteredRows.some((r: any) => r.__tracked),
    [filteredRows]
  );

  const activeColumns = useMemo<SheetColumn[]>(() => {
    const hidden = new Set<string>();
    if (share?.hide_money_columns) MONEY_COLUMN_IDS.forEach((id) => hidden.add(id));
    if (share?.hide_checkin_columns) CHECKIN_COLUMN_IDS.forEach((id) => hidden.add(id));
    const cols = COLUMNS.filter(
      (c) =>
        !c.hiddenByDefault &&
        !hidden.has(c.id) &&
        !(readOnly && c.kind === "action" && c.id !== "__t") &&
        // Confirming a team is an organiser decision, never a share recipient's.
        !(shareToken && c.id === "__confirm") &&
        !(c.id === "__confirm" && !hasTrackedVisible)
    );
    // Row actions always lead.
    const actions = cols.filter((c) => c.kind === "action");
    return [...actions, ...cols.filter((c) => c.kind !== "action")];
  }, [share, readOnly, shareToken, hasTrackedVisible]);

  const gridColumns = useMemo<GridColumn[]>(
    () =>
      activeColumns.map((c) => ({
        id: c.id,
        title:
          sort?.id === c.id ? `${c.title} ${sort.dir === "asc" ? "▲" : "▼"}` : c.title,
        width: colWidths[c.id] ?? c.width ?? 140,
      })),
    [activeColumns, colWidths, sort]
  );

  const onHeaderClicked = useCallback(
    (col: number) => {
      const def = activeColumns[col];
      // Every data column sorts; the row-action columns aren't data.
      if (!def || def.kind === "action") return;
      // asc → desc → cleared, like a spreadsheet.
      setSort((prev) => {
        if (prev?.id !== def.id) return { id: def.id, dir: "asc" };
        if (prev.dir === "asc") return { id: def.id, dir: "desc" };
        return null;
      });
    },
    [activeColumns]
  );


  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const def = activeColumns[col];
      const record = visibleRows[row];

      if (!def || !record) return { kind: GridCellKind.Loading, allowOverlay: false };

      const tracked = Boolean(record.__tracked);
      const comped = Boolean(record.comped_at);

      if (def.kind === "action") {
        // Drawn as a badge in drawCell — the text is only for copy.
        const tone =
          def.id === "__payments" ? payTone(record.payment_status) : { bg: "#e2e8f0", fg: "#334155" };
        return {
          kind: GridCellKind.Text,
          data:
            def.id === "__t"
              ? tracked
                ? "Tracked"
                : ""
              : def.id === "__withdraw"
              ? record.__withdrawn
                ? "Restore team"
                : "Withdraw team"
              : def.id === "__confirm"
              ? tracked
                ? "Confirm as playing"
                : ""
              : def.id === "__comp"
              ? comped
                ? "Remove comp"
                : "Comp this team"
              : "Record payment",
          displayData: "",
          allowOverlay: false,
          readonly: true,
          themeOverride: { bgCell: tracked ? "#f8fafc" : "#ffffff", textDark: tone.fg },
        };

      }


      const key = `${record.id}::${def.id}` as EditKey;
      const dirty = edits.has(key);
      // Amount Owed follows the ledger-derived paid total; it is never typed in.
      const raw =
        def.id === "amount_owed"
          ? comped
            ? 0
            : Math.max(
                0,
                num(record.total_amount_due) +
                  num(record.processing_fee) +
                  num(record.late_fee_charged) -
                  num(record.total_amount_paid)
              )
          : def.id === "payment_status" && comped
          ? "Comped"
          : dirty
          ? edits.get(key)
          : record[def.id];
      const owedDirty = false;
      const failed = failedRows.has(String(record.id));
      const lockReason = def.guard ? def.guard(record) : null;
      // Tracked teams write to event_team_tracking, so only the fields that
      // table actually has are editable on those rows.
      const editable =
        (def.editable ?? false) &&
        EDITABLE.has(def.id) &&
        !lockReason &&
        (!tracked || TRACKED_EDITABLE.has(def.id));




      const withdrawn = Boolean(record.__withdrawn);
      const themeOverride = failed
        ? { bgCell: "#fdeaea", textDark: "#8c1d1d" }
        : dirty || owedDirty
        ? { bgCell: "#fff8e1" }
        : withdrawn
        ? { bgCell: "#f5f5f4", textDark: "#a1a1aa" }
        : comped && (def.id === "payment_status" || def.id === "amount_owed")
        ? { bgCell: "#dcfce7", textDark: "#166534" }
        : tracked
        ? { bgCell: "#f8fafc", textDark: "#475569" }
        : lockReason
        ? { bgCell: "#f3f4f6", textDark: "#6b7280" }
        : editable
        ? undefined
        : { bgCell: "#fafbfc" };



      if (def.optionsKey) {
        return {
          kind: GridCellKind.Custom,
          allowOverlay: editable,
          readonly: !editable,
          copyData: String(raw ?? ""),
          // Editable pickers get a faint tint (plus a chevron in drawCell) so
          // they don't read as plain text.
          themeOverride: themeOverride ?? (editable ? { bgCell: "#f5f8ff" } : undefined),
          data: {
            kind: "simple-dropdown-cell",
            allowedValues: options[def.optionsKey] ?? [],
            value: raw == null ? "" : String(raw),
          },
        } as GridCell;
      }


      switch (def.kind) {
        case "boolean":
          return {
            kind: GridCellKind.Boolean,
            data: Boolean(raw),
            allowOverlay: false,
            readonly: !editable,
            themeOverride,
          };
        case "money":
          return {
            kind: GridCellKind.Number,
            data: raw == null || raw === "" ? undefined : Number(raw),
            displayData: asMoney(raw),
            allowOverlay: editable,
            readonly: !editable,
            fixedDecimals: 2,
            themeOverride,
          };

        case "date":
          return {
            kind: GridCellKind.Text,
            data: fmtDate(raw),
            displayData: fmtDate(raw) || "—",
            allowOverlay: false,
            readonly: true,
            themeOverride,
          };
        default: {
          // Phones display formatted; the editor opens on the same formatted
          // text and is normalised back to E.164 on save.
          const text =
            raw == null
              ? ""
              : def.id === "coach_phone"
              ? fmtPhone(raw)
              : String(raw);
          return {
            kind: GridCellKind.Text,
            data: text,
            displayData:
              withdrawn && def.id === "team_name" ? `${text}  · Withdrawn` : text,
            allowOverlay: editable || EXPANDABLE_COLUMN_IDS.has(def.id),
            readonly: !editable,
            themeOverride,
          };
        }
      }
    },
    [visibleRows, edits, failedRows, options, activeColumns]
  );

  const extractValue = (cell: EditableGridCell): unknown => {
    if (cell.kind === GridCellKind.Custom) {
      return (cell.data as { value?: string })?.value ?? "";
    }
    if ("data" in cell) return cell.data;
    return undefined;
  };

  const applyEdit = useCallback(
    (item: Item, cell: EditableGridCell, draft: Map<EditKey, unknown>) => {
      const [col, row] = item;
      if (readOnly) return;
      const def = activeColumns[col];
      const record = visibleRows[row];
      if (!def?.editable || !EDITABLE.has(def.id) || !record) return;
      if (record.__tracked && !TRACKED_EDITABLE.has(def.id)) {
        toast.error("That field only applies once the team has registered.");
        return;
      }

      if (def.guard?.(record)) {
        toast.error(def.guard(record) as string);
        return;
      }

      const raw = extractValue(cell);
      // 816-223-7452 and +18162237452 both store as +18162237452.
      const value = def.id === "coach_phone" ? normalizePhoneInput(raw) : raw;

      draft.set(`${record.id}::${def.id}` as EditKey, value);



    },
    [visibleRows, activeColumns, readOnly]

  );

  const onCellEdited = useCallback(
    (item: Item, cell: EditableGridCell) => {
      setEdits((prev) => {
        const draft = new Map(prev);
        applyEdit(item, cell, draft);
        return draft;
      });
    },
    [applyEdit]
  );

  const onCellsEdited = useCallback(
    (newValues: readonly { location: Item; value: EditableGridCell }[]) => {
      const inGrid = newValues;
      setEdits((prev) => {
        const draft = new Map(prev);
        for (const { location, value } of inGrid) applyEdit(location, value, draft);
        return draft;
      });
      return true;
    },
    [applyEdit]
  );


  /* ---------------------------- save ---------------------------- */

  const save = useCallback(async () => {
    if (edits.size === 0) return;
    setSaving(true);
    setStatus(null);

    const trackedIds = new Set(
      rows.filter((r) => r.__tracked).map((r) => String(r.id))
    );

    const byRow = new Map<string, Record<string, unknown>>();
    const trackedByRow = new Map<string, Record<string, unknown>>();
    for (const [key, value] of edits) {
      const [id, field] = key.split("::");
      if (trackedIds.has(id)) {
        // Tracked rows aren't registrations — they update their own table.
        const col = TRACKED_FIELD_MAP[field];
        if (!col) continue;
        const patch = trackedByRow.get(id) ?? {};
        patch[col] = value === "" ? null : value;
        trackedByRow.set(id, patch);
        continue;
      }
      const patch = byRow.get(id) ?? {};
      patch[field] = value;
      byRow.set(id, patch);
    }

    for (const [id, patch] of trackedByRow) {
      const { error: tErr } = await supabase.from(TRACKING_TABLE).update(patch).eq("id", id);
      if (tErr) {
        setSaving(false);
        setStatus(`Save failed on a tracked team: ${tErr.message}. Your edits are still here.`);
        return;
      }
    }

    if (byRow.size === 0) {
      setEdits(new Map());
      setSaving(false);
      setStatus("Saved.");
      await reload();
      return;
    }

    const { data, error } = await supabase.functions.invoke("grid-batch-update", {
      body: {
        table: TABLE,
        event_id: eventId,
        share_token: shareToken,
        updates: [...byRow].map(([id, patch]) => ({ id, patch })),
      },
    });


    if (error) {
      setSaving(false);
      setStatus(`Save failed: ${error.message}. Your edits are still here.`);
      return;
    }

    const results: { id: string; ok: boolean; error?: string }[] = data?.results ?? [];
    const failures = new Map<string, string>();
    const succeeded = new Set<string>();
    for (const r of results) {
      if (r.ok) succeeded.add(r.id);
      else failures.set(r.id, r.error ?? "Rejected");
    }

    setEdits((prev) => {
      const draft = new Map(prev);
      for (const key of prev.keys()) {
        if (succeeded.has(key.split("::")[0])) draft.delete(key);
      }
      return draft;
    });
    setFailedRows(failures);
    setSaving(false);

    const firstError = [...failures.values()][0];
    setStatus(
      failures.size === 0
        ? `Saved ${succeeded.size} ${succeeded.size === 1 ? "row" : "rows"}.${
            data?.audit_warning ? " (Audit log write failed.)" : ""
          }`
        : `Saved ${succeeded.size}. ${failures.size} rejected — ${firstError}`
    );

    if (succeeded.size > 0) reload();
  }, [edits, eventId, reload, rows]);

  const discard = useCallback(() => {
    setEdits(new Map());
    setFailedRows(new Map());
    setStatus(null);
  }, []);

  const exportCsv = useCallback(() => {
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    // The row action isn't data — it has no place in an export.
    const cols = activeColumns.filter((c) => c.kind !== "action");
    const lines = [cols.map((c) => esc(c.title)).join(",")];
    for (const r of visibleRows) {
      lines.push(
        cols.map((c) => {

          const key = `${r.id}::${c.id}` as EditKey;
          const raw = edits.has(key) ? edits.get(key) : r[c.id];
          if (c.id === "coach_phone") return esc(fmtPhone(raw));
          if (c.kind === "money") return esc(raw == null ? "" : Number(raw).toFixed(2));
          if (c.kind === "date") return esc(fmtDate(raw));
          if (c.kind === "boolean") return esc(raw ? "Yes" : "No");
          return esc(raw);
        }).join(",")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // The filter decides the contents, so it decides the name too.
    a.download =
      teamFilter === "active"
        ? "active-teams.csv"
        : teamFilter === "withdrawn"
        ? "withdrawn-teams.csv"
        : "registered-teams.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [visibleRows, edits, activeColumns, teamFilter]);

  useEffect(() => {
    if (edits.size === 0) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [edits.size]);

  /* --------------------------- render --------------------------- */

  const dirtyCount = edits.size;

  /**
   * Canvas post-draw: a coloured $ badge on the action column and a chevron on
   * dropdown cells so pickers are recognisable before you click them.
   */
  const drawCell = useCallback(
    (args: any, drawContent: () => void) => {
      const def = activeColumns[args.col];
      const { ctx, rect } = args;

      if (def?.kind === "action") {
        const record = visibleRows[args.row];
        // The blank creation row has no actions.
        if (!record) return;
        const comped = Boolean(record.comped_at);
        const isTracked = Boolean(record.__tracked);

        // Gutter flag: a filled circled "T" badge marks a tracked team, nothing
        // on registered rows. Costs 28px instead of a whole "Source" column.
        if (def.id === "__t") {
          if (!isTracked) return;
          drawTrackedBadge(ctx, rect, 18);
          return;
        }

        // Withdraw / restore reads as a word, not a glyph.
        if (def.id === "__withdraw") {
          const isWithdrawn = Boolean(record.__withdrawn);
          const tone = isWithdrawn
            ? { bg: "#fee2e2", fg: "#b91c1c" }
            : { bg: "#e2e8f0", fg: "#334155" };
          const label = isWithdrawn ? "Withdrawn" : "Withdraw";
          ctx.save();
          ctx.font = "600 11px sans-serif";
          const w = Math.min(rect.width - 10, ctx.measureText(label).width + 16);
          const h = 18;
          const x = rect.x + (rect.width - w) / 2;
          const y = rect.y + (rect.height - h) / 2;
          ctx.fillStyle = tone.bg;
          ctx.beginPath();
          (ctx.roundRect ?? ctx.rect).call(ctx, x, y, w, h, 9);
          ctx.fill();
          ctx.fillStyle = tone.fg;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(label, x + w / 2, y + h / 2 + 0.5);
          ctx.restore();
          return;
        }

        // "Confirm" only exists on tracked rows — a registration is already real.

        if (def.id === "__confirm") {
          if (!isTracked) return;
          const label = "Confirm";
          ctx.save();
          ctx.font = "600 11px sans-serif";
          const w = Math.min(rect.width - 8, ctx.measureText(label).width + 16);
          const h = 18;
          const x = rect.x + (rect.width - w) / 2;
          const y = rect.y + (rect.height - h) / 2;
          ctx.fillStyle = "#dbeafe";
          ctx.beginPath();
          (ctx.roundRect ?? ctx.rect).call(ctx, x, y, w, h, 9);
          ctx.fill();
          ctx.fillStyle = "#1d4ed8";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(label, x + w / 2, y + h / 2 + 0.5);
          ctx.restore();
          return;
        }

        const tone =
          def.id === "__payments"
            ? payTone(record?.payment_status)
            : def.id === "__comp" && comped
            ? { bg: "#dcfce7", fg: "#166534" }
            : { bg: "#e2e8f0", fg: "#334155" };
        // Tracked teams have no registration to comp, rename or pay against.
        if (isTracked) return;
        const glyph = def.id === "__comp" ? "★" : "$";

        const size = 22;
        const cx = rect.x + rect.width / 2;
        const cy = rect.y + rect.height / 2;
        ctx.save();
        ctx.fillStyle = tone.bg;
        ctx.beginPath();
        (ctx.roundRect ?? ctx.rect).call(
          ctx,
          cx - size / 2,
          cy - size / 2,
          size,
          size,
          6
        );
        ctx.fill();
        ctx.fillStyle = tone.fg;
        ctx.font = "600 13px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(glyph, cx, cy + 0.5);
        ctx.restore();
        return;
      }


      drawContent();

      // A withdrawn team's name is struck through, so a dropout reads at a glance.
      if (def?.id === "team_name" && visibleRows[args.row]?.__withdrawn) {
        const text = String(visibleRows[args.row]?.team_name ?? "");
        if (text) {
          ctx.save();
          ctx.font = "13px sans-serif";
          const w = Math.min(rect.width - 16, ctx.measureText(text).width);
          const y = rect.y + rect.height / 2 + 0.5;
          ctx.strokeStyle = "#a1a1aa";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(rect.x + 8, y);
          ctx.lineTo(rect.x + 8 + w, y);
          ctx.stroke();
          ctx.restore();
        }
      }

      if (def?.optionsKey && def.editable) {
        const x = rect.x + rect.width - 12;
        const y = rect.y + rect.height / 2 - 1;
        ctx.save();
        ctx.strokeStyle = "#64748b";
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(x - 4, y - 2);
        ctx.lineTo(x, y + 2);
        ctx.lineTo(x + 4, y - 2);
        ctx.stroke();
        ctx.restore();
      }
    },
    [activeColumns, visibleRows]
  );

  /**
   * Canvas header post-draw: put the same circled-T legend on the gutter column
   * so the blank header above the row numbers becomes self-explanatory.
   */
  const drawHeader = useCallback(
    (args: any, drawContent: () => void) => {
      if (args.column?.id === "__t") {
        drawTrackedBadge(args.ctx, args.rect, 18);
        return;
      }
      drawContent();
    },
    []
  );

  const StatCard = ({
    label,
    value,
    tone,
  }: {
    label: string;
    value: number | string;
    tone?: string;
  }) => (

    <div className="rounded-md border bg-card px-3 py-1.5 leading-tight">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-base font-semibold ${tone ?? ""}`}>{value}</div>
    </div>
  );



  if (denied) {
    return (
      <div className="flex h-full items-center justify-center p-10">
        <div className="max-w-md rounded-md border bg-card p-6 text-center">
          <div className="text-base font-semibold">This sheet isn't available</div>
          <p className="mt-2 text-sm text-muted-foreground">{denied}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={shellRef} className="flex h-full min-h-0 flex-col gap-3">
      {share && (
        <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
          Shared by {share.organization_name}
          {" · "}
          {share.permission === "edit" ? "view and edit" : "view only"}
          {share.expires_at
            ? ` · expires ${new Date(share.expires_at).toLocaleDateString()}`
            : " · no expiry"}
          {share.label ? ` · ${share.label}` : ""}
        </div>
      )}
      {/* Stats bar */}
      {(
        <div className="flex flex-wrap items-center gap-2">
          <StatCard label="Total Teams" value={stats.total} />
          {stats.tracked > 0 && (
            <StatCard label="Tracked" value={stats.tracked} tone="text-slate-500" />
          )}
          <StatCard label="Paid" value={stats.paid} tone="text-emerald-600" />
          <StatCard label="Pending" value={stats.pending} tone="text-amber-600" />
          {stats.withdrawn > 0 && (
            <StatCard label="Withdrawn" value={stats.withdrawn} tone="text-rose-600" />
          )}
          {stats.refundsOwedCount > 0 && (
            <StatCard
              label="Refunds owed"
              value={`${stats.refundsOwedCount} · ${new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(stats.refundsOwedTotal)}`}
              tone="text-amber-600"
            />
          )}

          {ageGroups.length > 0 && <div className="mx-1 h-8 w-px bg-border" />}
          {ageGroups.map((g) => (
            <StatCard key={g} label={g} value={stats.byAge[g] ?? 0} />
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search team, coach, email, division, rate…"
          className="h-9 w-72 rounded-md border border-input bg-background px-3 text-sm"
        />
        <div className="inline-flex h-9 overflow-hidden rounded-md border border-input text-sm">
          {(["active", "withdrawn", "all"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setTeamFilter(f)}
              className={`px-3 font-medium capitalize ${
                teamFilter === f ? "bg-primary text-primary-foreground" : "bg-background"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        {!readOnly && (
        <button
          type="button"
          onClick={save}
          disabled={saving || dirtyCount === 0}
          className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {saving ? "Saving…" : `Save changes${dirtyCount ? ` (${dirtyCount})` : ""}`}
        </button>
        )}
        {!readOnly && (
        <button
          type="button"
          onClick={discard}
          disabled={saving || dirtyCount === 0}
          className="inline-flex h-9 items-center rounded-md border border-input px-4 text-sm font-medium disabled:opacity-50"
        >
          Discard
        </button>
        )}
        {!readOnly && !shareToken && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            title="Add tracked teams by pasting a list or typing one at a time"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> Add teams
          </button>
        )}
        <button
          type="button"
          onClick={exportCsv}
          className="inline-flex h-9 items-center rounded-md border border-input px-4 text-sm font-medium"
        >
          Teams & payments
        </button>
        {!shareToken && (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            title="Create a scoped, expiring link to this sheet"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
        )}
        {!popout && !shareToken && (
          <button
            type="button"
            onClick={openInNewWindow}
            title="Open this sheet in its own window"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input px-4 text-sm font-medium"
          >
            <ExternalLink className="h-4 w-4" /> Open in new window
          </button>
        )}

        {loading && <span className="text-sm text-muted-foreground">Loading…</span>}
        {status && <span className="text-sm text-muted-foreground">{status}</span>}
      </div>

      {/* DataEditor will not size itself — this parent must have height. */}
      <div
        ref={gridWrapperRef}
        className="min-h-0 flex-1 overflow-hidden rounded-md border"
        onMouseLeave={() => setTrackedHeaderTip(null)}
      >
        <DataEditor
          columns={gridColumns}
          rows={visibleRows.length}
          getCellContent={getCellContent}
          onCellEdited={onCellEdited}
          onCellsEdited={onCellsEdited}
          onHeaderClicked={onHeaderClicked}
          drawCell={drawCell}
          drawHeader={drawHeader}
          gridSelection={selection}
          onGridSelectionChange={setSelection}
          onItemHovered={(args) => {
            const { location: [col, row] } = args;
            const def = activeColumns[col];
            const record = visibleRows[row];
            const reason = record?.comp_reason ? String(record.comp_reason) : "";
            setCompTip(def?.id === "__comp" && record?.comped_at && reason ? reason : null);
            setWithdrawTip(
              record?.__withdrawn
                ? String(record.__withdraw_reason || "No reason recorded")
                : null
            );
            // Clipped columns show their whole value on hover.
            const tipText =
              def && record && EXPANDABLE_COLUMN_IDS.has(def.id)
                ? String(record[def.id] ?? "").trim()
                : "";
            setCellTip(tipText ? { title: def!.title, text: tipText } : null);
            if (args.kind === "header" && def?.id === "__t" && gridWrapperRef.current) {
              const wrapperRect = gridWrapperRef.current.getBoundingClientRect();
              setTrackedHeaderTip({
                x: wrapperRect.left + args.bounds.x + args.bounds.width / 2,
                y: wrapperRect.top + args.bounds.y + args.bounds.height + 4,
              });
            } else {
              setTrackedHeaderTip(null);
            }
          }}
          onCellClicked={([col, row]) => {
            const def = activeColumns[col];
            const record = visibleRows[row];
            if (!record) return;
            // Long read-only text opens in full rather than staying clipped.
            if (def && EXPANDABLE_COLUMN_IDS.has(def.id)) {
              const text = String(record[def.id] ?? "").trim();
              if (text) {
                setExpandCell({
                  title: `${def.title} — ${String(record.team_name ?? "")}`,
                  text,
                });
              }
              return;
            }
            if (def?.kind !== "action" || def.id === "__t") return;
            if (def.id === "__withdraw") {
              setWithdrawRow(record);
              return;
            }
            if (def.id === "__confirm") {
              if (record.__tracked) setConfirmRow(record.__raw ?? record);
              return;
            }
            if (record.__tracked) return;

            if (def.id === "__comp") {
              setCompRow(record);
              return;
            }
            setPayRow({ id: String(record.id), name: String(record.team_name ?? "") });
          }}

          customRenderers={CUSTOM_CELLS}

          rowMarkers="number"
          rowHeight={30}
          headerHeight={34}
          freezeColumns={4}
          theme={{
            bgHeader: "#f1f5f9",
            textHeader: "#0f172a",
            headerFontStyle: "600 12px",
            baseFontStyle: "13px",
            borderColor: "#e5e9ef",
            horizontalBorderColor: "#e5e9ef",
          }}
          smoothScrollX
          smoothScrollY
          fillHandle
          getCellsForSelection
          keybindings={{ search: true }}
          onColumnResize={(col, newSize) =>
            setColWidths((prev) => ({ ...prev, [col.id as string]: newSize }))
          }
          width="100%"
          height="100%"
        />
      </div>

      <RegistrationPaymentDialog
        registrationId={payRow?.id ?? null}
        teamName={payRow?.name}
        open={payRow !== null}
        onOpenChange={(o) => !o && setPayRow(null)}
        onChanged={reload}
      />

      <CompRegistrationDialog
        registrationId={compRow ? String(compRow.id) : null}
        teamName={String(compRow?.team_name ?? "")}
        comped={Boolean(compRow?.comped_at)}
        balance={Math.max(
          0,
          num(compRow?.total_amount_due) +
            num(compRow?.processing_fee) +
            num(compRow?.late_fee_charged) -
            num(compRow?.total_amount_paid)
        )}
        compReason={compRow?.comp_reason ? String(compRow.comp_reason) : null}
        open={compRow !== null}
        onOpenChange={(o) => !o && setCompRow(null)}
        onChanged={reload}
      />

      <WithdrawTeamDialog
        registrationId={
          withdrawRow && !withdrawRow.__tracked ? String(withdrawRow.id) : null
        }
        trackingId={withdrawRow?.__tracked ? String(withdrawRow.id) : null}
        teamName={String(withdrawRow?.team_name ?? "")}
        withdrawn={Boolean(withdrawRow?.__withdrawn)}
        amountPaid={num(withdrawRow?.total_amount_paid)}
        open={withdrawRow !== null}
        onOpenChange={(o) => !o && setWithdrawRow(null)}
        onChanged={reload}
      />

      <ConfirmTrackedTeamDialog
        eventId={eventId}
        tracked={confirmRow}
        open={confirmRow !== null}
        onOpenChange={(o) => !o && setConfirmRow(null)}
        onConfirmed={reload}
      />


      <ShareSheetDialog
        eventId={eventId}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />

      <AddTeamsDialog
        open={addOpen}
        busy={adding}
        eventId={eventId}
        ageGroups={ageGroups}
        onOpenChange={setAddOpen}
        onAdd={handleAddTeams}
      />

      {withdrawTip && (
        <div className="pointer-events-none fixed bottom-16 right-4 z-50 max-w-xs rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-50 shadow-lg">
          Withdrawn — {withdrawTip}
        </div>
      )}

      {compTip && (
        <div className="pointer-events-none fixed bottom-4 right-4 z-50 max-w-xs rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-50 shadow-lg">
          Comped — {compTip}
        </div>
      )}

      {trackedHeaderTip && (
        <div
          className="pointer-events-none fixed z-50 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-50 shadow-lg"
          style={{ left: trackedHeaderTip.x, top: trackedHeaderTip.y }}
        >
          Tracked — added by the organiser, not yet registered.
        </div>
      )}

      {cellTip && !expandCell && (
        <div className="pointer-events-none fixed bottom-40 right-4 z-50 max-w-sm rounded-md bg-slate-900 px-3 py-2 text-xs text-slate-50 shadow-lg">
          <div className="mb-0.5 font-semibold uppercase tracking-wide text-slate-400">
            {cellTip.title}
          </div>
          <div className="whitespace-pre-wrap">{cellTip.text}</div>
          <div className="mt-1 text-slate-400">Click the cell to open it.</div>
        </div>
      )}

      <Dialog open={expandCell !== null} onOpenChange={(o) => !o && setExpandCell(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">{expandCell?.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto text-sm">
            {(expandCell?.text ?? "")
              .split(" · ")
              .filter(Boolean)
              .map((line, i) => (
                <div key={i} className="rounded-md border bg-muted/40 px-3 py-2">
                  {line}
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>


  );

}
